
import { useState } from 'react';

interface ComboFaqProps {
  lang: 'en' | 'ja';
}

export default function ComboFaq({ lang }: ComboFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const t = {
    en: {
      badge: 'FAQ',
      title: 'Frequently Asked Questions',
      items: [
        {
          q: 'Do I need to speak Japanese?',
          a: 'Not at all! Your local guide handles all communication with venues. You just relax and enjoy.',
        },
        {
          q: 'How do I pay for food and drinks?',
          a: 'Food, drinks, and karaoke room fees are paid directly at each venue. Our service fee (¥30,000 base) is separate.',
        },
        {
          q: 'Can I choose the izakaya and karaoke venue?',
          a: 'We hand-pick the best spots based on your preferences and availability. Feel free to share any requests in advance!',
        },
        {
          q: 'Is alcohol included in the price?',
          a: 'No. Alcohol is served by each venue and paid directly there. Our service fee covers the guide and experience only.',
        },
        {
          q: 'What if I don\'t drink alcohol?',
          a: 'No problem at all! Izakayas have plenty of non-alcoholic options. Just let us know and we\'ll make sure you\'re comfortable.',
        },
        {
          q: 'How do I book?',
          a: 'Simply send us a WhatsApp message with your preferred date, number of guests, and any special requests. We\'ll confirm availability within 24 hours.',
        },
        {
          q: 'Is this a transportation service?',
          a: 'No. This service is not a transportation service. We walk between venues together as companions.',
        },
      ],
    },
    ja: {
      badge: 'よくある質問',
      title: 'よくある質問',
      items: [
        {
          q: '日本語が話せなくても大丈夫ですか？',
          a: 'もちろんです！ガイドが全ての店舗とのやり取りを担当します。あなたはリラックスして楽しむだけ。',
        },
        {
          q: '飲食代はどうやって支払いますか？',
          a: '飲食代・カラオケ部屋代は各店舗へ直接お支払いください。当サービスの料金（ベース¥30,000）とは別になります。',
        },
        {
          q: '居酒屋やカラオケ店を選べますか？',
          a: 'ご希望や空き状況に合わせて最適な場所を厳選します。事前にリクエストがあればお気軽にどうぞ！',
        },
        {
          q: 'お酒は料金に含まれていますか？',
          a: 'いいえ。酒類の提供は各店舗が行い、現地でのお支払いとなります。当サービスの料金はガイドと体験のみです。',
        },
        {
          q: 'お酒が飲めなくても参加できますか？',
          a: 'もちろんです！居酒屋にはノンアルコールの選択肢も豊富にあります。事前にお知らせいただければ対応します。',
        },
        {
          q: '予約方法を教えてください。',
          a: 'WhatsAppで希望日・参加人数・ご要望をお送りください。24時間以内に空き状況をご確認してご連絡します。',
        },
        {
          q: 'これは運送サービスですか？',
          a: 'いいえ。当サービスは運送業ではありません。会場間は同行者として一緒に歩いて移動します。',
        },
      ],
    },
  }[lang];

  return (
    <section className="py-20 px-4 bg-gray-950 text-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-question-line text-amber-400 text-sm"></i>
            <span className="text-gray-300 text-sm">{t.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">{t.title}</h2>
        </div>

        <div className="space-y-3">
          {t.items.map((item, i) => (
            <div
              key={i}
              className="bg-white/5 border border-white/10 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-white/5 transition-colors duration-200 cursor-pointer"
              >
                <span className="text-white font-medium text-sm md:text-base">{item.q}</span>
                <i className={`ri-arrow-down-s-line text-amber-400 text-xl flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`}></i>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5">
                  <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
